import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule } from '@angular/forms';
import {
  parkingCoordinateFormConfig,
  parkingLocationFormConfig,
} from '../../../settings/parking/configs';
import {
  FormBuilderService,
  IFormGroupConfig,
  UIBaseModal,
  UIButton,
  UIFormModule,
  UIIcon,
  UIModal,
  UIModalService,
} from '@shared/ui';
import { catchError } from 'rxjs';
import { ParkingService } from '../../services';
import { parkingFormConfig } from '../../configs';
import { ParkingMapModal } from '../../../settings/parking/components/parking-map-modal/parking-map-modal.component';
import { IParkingLocationModel } from '@aggregator/core';

type LocationData = { x: number; y: number; z?: number } | { lat: number; lon: number };

@Component({
  selector: 'aggregator-operator-parking-details-modal',
  templateUrl: './operator-parking-details-modal.component.html',
  styleUrl: './operator-parking-details-modal.component.scss',
  standalone: true,
  imports: [UIModal, UIFormModule, UIButton, UIIcon, FormsModule],
  providers: [ParkingService],
})
export class OperatorParkingDetailsModal extends UIBaseModal implements OnInit {
  isLoadingData = false;
  isSaving = false;

  form?: FormGroup;
  formConfig: IFormGroupConfig = parkingFormConfig;

  formLocation?: FormGroup;
  formLocationConfig: IFormGroupConfig = parkingLocationFormConfig;

  formCoordinate?: FormGroup;
  formCoordinateConfig: IFormGroupConfig = parkingCoordinateFormConfig;

  coordinates: number[][] = [];
  formLocationArray: FormGroup[] = [];

  get canNotSave(): boolean {
    return this.form?.invalid || !this.form?.dirty;
  }

  get isDetails(): boolean {
    return !!this.data;
  }

  get modalTitle(): string {
    return this.isDetails ? 'Изменить данные' : 'Добавить';
  }

  get itemId(): string {
    return this.data as string;
  }

  constructor(
    private formBuilder: FormBuilderService,
    private service: ParkingService,
    private destroy$: DestroyRef,
    private modalService: UIModalService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.form = this.formBuilder.build(this.formConfig);
    this.formLocation = this.formBuilder.build(this.formLocationConfig);
    this.formCoordinate = this.formBuilder.build(this.formCoordinateConfig);

    if (this.itemId) {
      this.isLoadingData = true;
      this.service
        .getById(this.itemId)
        .pipe(takeUntilDestroyed(this.destroy$))
        .subscribe(response => {
          const data = response?.value;
          if (data) {
            const { location, ...formData } = data;

            this.form?.patchValue({
              ...formData,
              priceForHour: new Number((data.tariffs ?? [])[0] || 0).valueOf(),
            });

            if (location) {
              const locationData = location as LocationData;

              if ('x' in locationData && 'y' in locationData) {
                this.formLocation?.patchValue({
                  location: { x: locationData.x, y: locationData.y },
                });
              } else if ('lat' in locationData && 'lon' in locationData) {
                this.formLocation?.patchValue({
                  location: { x: locationData.lon, y: locationData.lat },
                });
              }
            }
          }

          this.form?.disable();
          this.formLocation?.disable();
          this.formCoordinate?.disable();

          this.isLoadingData = false;
        });
    }
  }

  onSave(): void {
    if (this.isSaving || this.canNotSave) return;

    this.isSaving = true;
    this.form?.disable();

    const formData = this.form?.getRawValue();
    formData.typeId = formData.type;
    formData.categoryId = formData.category;
    formData.totalSpots = new Number(formData.totalSpots);
    formData.priceForHour = new Number(formData.priceForHour);
    formData.type = undefined;
    formData.category = undefined;

    const locationFormData = this.formLocation?.getRawValue();
    if (locationFormData?.location?.x && locationFormData?.location?.y) {
      formData.location = {
        x: locationFormData.location.x,
        y: locationFormData.location.y,
        z: 0,
      };
    }

    if (this.coordinates.length) {
      formData.coordinates = this.coordinates;
    }

    (this.isDetails ? this.service.update(this.itemId, formData) : this.service.create(formData))
      .pipe(
        takeUntilDestroyed(this.destroy$),
        catchError(error => {
          this.isSaving = false;
          this.form?.enable();
          throw error;
        }),
      )
      .subscribe(() => this.onAfterClose(true));
  }

  onOpenMapModal(): void {
    const location = this.formLocation?.get('location')?.value || null;

    const mapLocation: IParkingLocationModel | null = location
      ? { x: location.x || 0, y: location.y || 0 }
      : null;

    this.modalService
      .create(ParkingMapModal, { coordinates: this.coordinates, location: mapLocation })
      .subscribe(result => {
        const modalResult = result as {
          location?: IParkingLocationModel | null;
          coordinates?: [number, number][][];
        };

        if (modalResult?.location) {
          this.formLocation?.get('location')?.setValue({
            x: modalResult.location.x,
            y: modalResult.location.y,
          });
        }

        if (modalResult?.coordinates?.length) {
          this.coordinates = modalResult.coordinates.flat();
          this.formLocationArray = this.coordinates.map(
            ([longitude, latitude], index) =>
              new FormGroup({
                label: new FormControl(`Точка ${index + 1}`),
                location: new FormGroup({
                  x: new FormControl(latitude),
                  y: new FormControl(longitude),
                }),
              }),
          );
        }
      });
  }
}
